import React, { useCallback, useEffect, useReducer, useState } from "react"
import { CadastrarEnderecoCliente } from "../../../services/cliente"
import type {
    AgendarNotificacaoPayload,
    AtualizarEnderecoPayload,
    EnderecoCliente,
    NovoEnderecoPayload,
} from "../../../services/edicaoCliente"
import {
    adicionarClienteCupomPainel,
    agendarNotificacaoClientePainel,
    atualizarClientePainel,
    atualizarEnderecoClientePainel,
    bloquearClientePainel,
    bonificarSelosClientePainel,
    excluirClientePainel,
    excluirEnderecoClientePainel,
    obterClientePainel,
    redefinirSenhaClientePainel,
} from "../../../services/edicaoCliente"
import useAppStore from "../../../stores/AppStore"
import useLoadingStore from "../../../stores/LoadingStore"
import { Loading } from "../../RegistradorDePedidos"
import { clientePainelParaModel, EdicaoClienteInicial } from "../models/edicaoClienteModel"
import { EdicaoClienteActionsTypes } from "./edicaoClienteActions"
import { EdicaoClienteContext } from "./EdicaoClienteContext"
import { edicaoClienteReducer } from "./edicaoClienteReducer"

type EdicaoClienteContextProviderProps = {
    idCliente: number
    children: React.ReactNode
}

export function EdicaoClienteContextProvider({
    idCliente,
    children,
}: EdicaoClienteContextProviderProps) {
    const [dados, dispatch] = useReducer(edicaoClienteReducer, EdicaoClienteInicial)
    const [erroCarregamento, setErroCarregamento] = useState<string | null>(null)
    const setLoading = useLoadingStore(s => s.setLoading)
    const configuracoes = useAppStore(s => s.configuracoes)

    const carregar = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        if (!idCliente || isNaN(idCliente)) {
            setErroCarregamento("Cliente inválido.")
            return
        }

        try {
            setLoading(true)
            const response = await obterClientePainel(autorizacao, idCliente)
            if ("retornoErro" in response) {
                setErroCarregamento(response.mensagem || "Erro ao carregar o cliente")
            } else {
                setErroCarregamento(null)
                dispatch({
                    type: EdicaoClienteActionsTypes.CARREGAR,
                    payload: clientePainelParaModel(response),
                })
            }
        } catch {
            setErroCarregamento("Erro ao carregar o cliente")
        } finally {
            setLoading(false)
        }
    }, [idCliente, setLoading])

    useEffect(() => {
        carregar()
    }, [carregar])

    const salvar = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) throw new Error("Sessão expirada. Faça login novamente.")

        try {
            setLoading(true)
            const response = await atualizarClientePainel(autorizacao, idCliente, {
                nome: dados.nome,
                cpf: dados.cpf || null,
                email: dados.email,
                telefone: dados.telefone || null,
                dataNascimento: dados.dataNascimento || null,
                taxaEntrega: dados.taxaEntrega || null,
                observacao: dados.observacao || null,
                vip: dados.vip,
                freteGratis: dados.freteGratis,
                pontuarNaFidelidade: dados.pontuarNaFidelidade,
                receberSpam: dados.receberSpam,
            })

            if ("retornoErro" in response) {
                throw new Error(response.mensagem || "Erro ao atualizar o cliente")
            }
            return "Configuração atualizada com sucesso!"
        } finally {
            setLoading(false)
        }
    }, [dados, idCliente, setLoading])

    const alterarSenha = useCallback(
        async (novaSenha: string, confirmacaoNovaSenha: string) => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) throw new Error("Sessão expirada. Faça login novamente.")

            try {
                setLoading(true)
                const response = await redefinirSenhaClientePainel(
                    autorizacao,
                    idCliente,
                    novaSenha,
                    confirmacaoNovaSenha
                )
                if ("retornoErro" in response) {
                    throw new Error(response.mensagem || "Erro ao alterar a senha")
                }
                return response.mensagem || "Senha alterada com sucesso!"
            } finally {
                setLoading(false)
            }
        },
        [idCliente, setLoading]
    )

    const salvarEndereco = useCallback(
        async (idEndereco: number, payload: AtualizarEnderecoPayload) => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) throw new Error("Sessão expirada. Faça login novamente.")

            try {
                setLoading(true)
                const response = await atualizarEnderecoClientePainel(
                    autorizacao,
                    idEndereco,
                    payload
                )
                if ("retornoErro" in response) {
                    throw new Error(response.mensagem || "Erro ao alterar o endereço")
                }
                dispatch({
                    type: EdicaoClienteActionsTypes.ATUALIZAR_ENDERECO,
                    payload: response,
                })
                return "Endereço alterado!"
            } finally {
                setLoading(false)
            }
        },
        [setLoading]
    )

    const excluirEndereco = useCallback(
        async (idEndereco: number) => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) throw new Error("Sessão expirada. Faça login novamente.")

            try {
                setLoading(true)
                const response = await excluirEnderecoClientePainel(autorizacao, idEndereco)
                if (response && "retornoErro" in response) {
                    throw new Error(response.mensagem || "Erro ao excluir o endereço")
                }
                dispatch({
                    type: EdicaoClienteActionsTypes.REMOVER_ENDERECO,
                    payload: { id: idEndereco },
                })
                return "Endereço excluído!"
            } finally {
                setLoading(false)
            }
        },
        [setLoading]
    )

    // Novo endereco: mesmo endpoint do cadastro de endereco do Registrador de Pedidos
    const adicionarEndereco = useCallback(
        async (payload: NovoEnderecoPayload) => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) throw new Error("Sessão expirada. Faça login novamente.")

            const parametros = configuracoes?.user?.parametros
            try {
                setLoading(true)
                const response = await CadastrarEnderecoCliente(autorizacao, {
                    appNome: parametros?.appNome,
                    idCliente,
                    idEstabelecimento: parametros?.estabelecimento?.id,
                    bairroEspecifico: parametros?.tipoEntregaBairro,
                    token: `${process.env.REACT_APP_CLIENTEFIEL_TOKEN}`,
                    cep: payload.cep || "00000000",
                    uf: payload.uf,
                    cidade: payload.cidade,
                    bairro: payload.bairro,
                    logradouro: payload.logradouro,
                    numero: payload.numero,
                    complemento: payload.complemento ?? "",
                    referencia: payload.referencia ?? "",
                    latitude: payload.latitude ?? undefined,
                    longitude: payload.longitude ?? undefined,
                })
                if (!response || response.retornoErro) {
                    throw new Error(response?.mensagem || "Erro ao cadastrar o endereço")
                }
                const novo: EnderecoCliente = {
                    id: response.id,
                    logradouro: response.logradouro ?? payload.logradouro,
                    numero: response.numero ?? payload.numero,
                    complemento: response.complemento ?? payload.complemento,
                    referencia: response.referencia ?? payload.referencia,
                    bairro: response.bairro ?? payload.bairro,
                    cidade: response.cidade ?? payload.cidade,
                    uf: response.uf ?? payload.uf,
                    cep: response.cep ?? payload.cep,
                    latitude: response.latitude ?? payload.latitude,
                    longitude: response.longitude ?? payload.longitude,
                }
                dispatch({ type: EdicaoClienteActionsTypes.ADICIONAR_ENDERECO, payload: novo })
                return "Endereço cadastrado!"
            } finally {
                setLoading(false)
            }
        },
        [configuracoes, idCliente, setLoading]
    )

    const bonificarSelos = useCallback(
        async (quantidade: number) => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) throw new Error("Sessão expirada. Faça login novamente.")

            try {
                setLoading(true)
                const response = await bonificarSelosClientePainel(
                    autorizacao,
                    idCliente,
                    quantidade
                )
                if ("retornoErro" in response) {
                    throw new Error(response.mensagem || "Erro ao bonificar o cliente")
                }
                return response.mensagem || "Selos gerados com sucesso!"
            } finally {
                setLoading(false)
            }
        },
        [idCliente, setLoading]
    )

    const adicionarAoCupom = useCallback(
        async (idCupom: number) => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) throw new Error("Sessão expirada. Faça login novamente.")

            try {
                setLoading(true)
                const response = await adicionarClienteCupomPainel(autorizacao, idCliente, idCupom)
                if ("retornoErro" in response) {
                    throw new Error(response.mensagem || "Erro ao adicionar o cliente ao cupom")
                }
                return response.mensagem || "Cliente foi adicionado ao cupom."
            } finally {
                setLoading(false)
            }
        },
        [idCliente, setLoading]
    )

    const agendarNotificacao = useCallback(
        async (payload: AgendarNotificacaoPayload) => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) throw new Error("Sessão expirada. Faça login novamente.")

            try {
                setLoading(true)
                const response = await agendarNotificacaoClientePainel(
                    autorizacao,
                    idCliente,
                    payload
                )
                if ("retornoErro" in response) {
                    throw new Error(response.mensagem || "Erro ao agendar a notificação")
                }
                return response.mensagem || "Notificação agendada!"
            } finally {
                setLoading(false)
            }
        },
        [idCliente, setLoading]
    )

    const alterarBloqueio = useCallback(
        async (bloquear: boolean) => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) throw new Error("Sessão expirada. Faça login novamente.")

            try {
                setLoading(true)
                const response = await bloquearClientePainel(autorizacao, idCliente, bloquear)
                if ("retornoErro" in response) {
                    throw new Error(
                        response.mensagem ||
                            (bloquear
                                ? "Erro ao bloquear o cliente"
                                : "Erro ao desbloquear o cliente")
                    )
                }
                dispatch({
                    type: EdicaoClienteActionsTypes.ATUALIZAR_ATIVO,
                    payload: { ativo: response.ativo },
                })
                return response.mensagem || "Cliente atualizado com sucesso!"
            } finally {
                setLoading(false)
            }
        },
        [idCliente, setLoading]
    )

    const excluirCliente = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) throw new Error("Sessão expirada. Faça login novamente.")

        try {
            setLoading(true)
            const response = await excluirClientePainel(autorizacao, idCliente)
            if ("retornoErro" in response) {
                throw new Error(response.mensagem || "Erro ao excluir o cliente")
            }
            return response.mensagem || "Cliente excluído com sucesso!"
        } finally {
            setLoading(false)
        }
    }, [idCliente, setLoading])

    return (
        <EdicaoClienteContext.Provider
            value={{
                dados,
                dispatch,
                erroCarregamento,
                salvar,
                alterarSenha,
                salvarEndereco,
                excluirEndereco,
                adicionarEndereco,
                bonificarSelos,
                adicionarAoCupom,
                agendarNotificacao,
                alterarBloqueio,
                excluirCliente,
            }}
        >
            <Loading />
            {children}
        </EdicaoClienteContext.Provider>
    )
}
